import { useEffect, useState } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, Tabs, Tab, IconButton, Box,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createSupplier, updateSupplier, getSupplier, type SupplierListItem, type SaveSupplierPayload } from '../../api/furnizori'
import { errorMessage } from '../../api/client'

const addressSchema = z.object({
  id: z.string().nullable(),
  type: z.enum(['Comanda', 'Livrare', 'Facturare']),
  line: z.string().min(1, 'Câmp obligatoriu'),
  city: z.string().optional(), county: z.string().optional(), postalCode: z.string().optional(), country: z.string().optional(),
})
const contactSchema = z.object({
  id: z.string().nullable(),
  name: z.string().min(1, 'Câmp obligatoriu'),
  jobTitle: z.string().optional(), phone: z.string().optional(), email: z.string().email('Email invalid').optional().or(z.literal('')),
})
const schema = z.object({
  code: z.string().min(1, 'Câmp obligatoriu').max(32),
  name: z.string().min(1, 'Câmp obligatoriu').max(256),
  cui: z.string().optional(), address: z.string().optional(), city: z.string().optional(), country: z.string().optional(),
  paymentTermDays: z.string().optional()
    .refine((v) => !v || (Number.isInteger(Number(v)) && Number(v) >= 0), 'Trebuie să fie un număr întreg pozitiv (0 sau mai mare)'),
  leadTimeDaysStandard: z.string().optional()
    .refine((v) => !v || (Number.isInteger(Number(v)) && Number(v) >= 0), 'Trebuie să fie un număr întreg pozitiv (0 sau mai mare)'),
  category: z.string().optional(), notes: z.string().optional(),
  addresses: z.array(addressSchema),
  contacts: z.array(contactSchema),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  code: '', name: '', cui: '', address: '', city: '', country: '',
  paymentTermDays: '', leadTimeDaysStandard: '', category: '', notes: '', addresses: [], contacts: [],
}

function numOrNull(v: string | undefined): number | null {
  if (v === undefined || v === '') return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

export default function SupplierFormDialog({ supplier, onClose }: { supplier: SupplierListItem | null | 'new'; onClose: () => void }) {
  const open = supplier !== null
  const editing = supplier !== null && supplier !== 'new'
  const qc = useQueryClient()
  const [tab, setTab] = useState(0)

  const { data: detail } = useQuery({
    queryKey: ['achizitii', 'furnizor', editing ? (supplier as SupplierListItem).id : null],
    queryFn: () => getSupplier((supplier as SupplierListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const addresses = useFieldArray({ control, name: 'addresses' })
  const contacts = useFieldArray({ control, name: 'contacts' })

  useEffect(() => {
    if (editing && detail) {
      reset({
        code: detail.code, name: detail.name, cui: detail.cui ?? '', address: detail.address ?? '',
        city: detail.city ?? '', country: detail.country ?? '',
        paymentTermDays: detail.paymentTermDays != null ? String(detail.paymentTermDays) : '',
        leadTimeDaysStandard: detail.leadTimeDaysStandard != null ? String(detail.leadTimeDaysStandard) : '',
        category: detail.category ?? '', notes: detail.notes ?? '',
        addresses: detail.addresses.map((a) => ({ ...a, city: a.city ?? '', county: a.county ?? '', postalCode: a.postalCode ?? '', country: a.country ?? '' })),
        contacts: detail.contacts.map((c) => ({ ...c, jobTitle: c.jobTitle ?? '', phone: c.phone ?? '', email: c.email ?? '' })),
      })
    } else if (supplier === 'new') {
      reset(EMPTY)
      setTab(0)
    }
  }, [supplier, editing, detail, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const paymentTermDays = numOrNull(values.paymentTermDays)
      const leadTimeDaysStandard = numOrNull(values.leadTimeDaysStandard)
      const payload: SaveSupplierPayload = {
        code: values.code, name: values.name,
        cui: values.cui || null, address: values.address || null, city: values.city || null, country: values.country || null,
        paymentTermDays: paymentTermDays != null ? Math.trunc(paymentTermDays) : null,
        leadTimeDaysStandard: leadTimeDaysStandard != null ? Math.trunc(leadTimeDaysStandard) : null,
        category: values.category || null, notes: values.notes || null,
        addresses: values.addresses.map((a) => ({ ...a, city: a.city || null, county: a.county || null, postalCode: a.postalCode || null, country: a.country || null })),
        contacts: values.contacts.map((c) => ({ ...c, jobTitle: c.jobTitle || null, phone: c.phone || null, email: c.email || null })),
      }
      if (editing) return updateSupplier((supplier as SupplierListItem).id, payload)
      return createSupplier(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['achizitii', 'furnizori'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare furnizor' : 'Furnizor nou'}</DialogTitle>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3 }}>
          <Tab label="General" />
          <Tab label={`Adrese (${addresses.fields.length})`} />
          <Tab label={`Contacte (${contacts.fields.length})`} />
        </Tabs>
        <DialogContent>
          {tab === 0 && (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Stack direction="row" spacing={2}>
                <Controller name="code" control={control} render={({ field }) => (
                  <TextField {...field} label="Cod" fullWidth error={!!errors.code} helperText={errors.code?.message} />
                )} />
                <Controller name="name" control={control} render={({ field }) => (
                  <TextField {...field} label="Denumire" fullWidth error={!!errors.name} helperText={errors.name?.message} />
                )} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Controller name="cui" control={control} render={({ field }) => (<TextField {...field} label="CUI/CIF" fullWidth />)} />
                <Controller name="category" control={control} render={({ field }) => (<TextField {...field} label="Categorie" fullWidth />)} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Controller name="address" control={control} render={({ field }) => (<TextField {...field} label="Adresă" fullWidth />)} />
                <Controller name="city" control={control} render={({ field }) => (<TextField {...field} label="Oraș" fullWidth />)} />
                <Controller name="country" control={control} render={({ field }) => (<TextField {...field} label="Țară" fullWidth />)} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Controller name="paymentTermDays" control={control} render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} type="number" label="Termen de plată (zile)" fullWidth
                    slotProps={{ htmlInput: { min: 0 } }} error={!!errors.paymentTermDays} helperText={errors.paymentTermDays?.message} />
                )} />
                <Controller name="leadTimeDaysStandard" control={control} render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} type="number" label="Timp mediu livrare (zile)" fullWidth
                    slotProps={{ htmlInput: { min: 0 } }} error={!!errors.leadTimeDaysStandard} helperText={errors.leadTimeDaysStandard?.message} />
                )} />
              </Stack>
              <Controller name="notes" control={control} render={({ field }) => (
                <TextField {...field} label="Note" fullWidth multiline minRows={2} />
              )} />
            </Stack>
          )}

          {tab === 1 && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              {addresses.fields.map((f, i) => (
                <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                  <Controller name={`addresses.${i}.type`} control={control} render={({ field }) => (
                    <TextField {...field} select label="Tip" sx={{ width: 150 }}
                      error={!!errors.addresses?.[i]?.type} helperText={errors.addresses?.[i]?.type?.message}>
                      <MenuItem value="Comanda">Comandă</MenuItem>
                      <MenuItem value="Livrare">Livrare</MenuItem>
                      <MenuItem value="Facturare">Facturare</MenuItem>
                    </TextField>
                  )} />
                  <Controller name={`addresses.${i}.line`} control={control} render={({ field }) => (
                    <TextField {...field} label="Adresă" fullWidth
                      error={!!errors.addresses?.[i]?.line} helperText={errors.addresses?.[i]?.line?.message} />
                  )} />
                  <Controller name={`addresses.${i}.city`} control={control} render={({ field }) => (<TextField {...field} label="Oraș" sx={{ width: 160 }} />)} />
                  <IconButton onClick={() => addresses.remove(i)}><DeleteOutlined fontSize="small" /></IconButton>
                </Stack>
              ))}
              <Box>
                <Button size="small" startIcon={<Add />}
                  onClick={() => addresses.append({ id: null, type: 'Comanda', line: '', city: '', county: '', postalCode: '', country: '' })}>
                  Adaugă adresă
                </Button>
              </Box>
            </Stack>
          )}

          {tab === 2 && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              {contacts.fields.map((f, i) => (
                <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                  <Controller name={`contacts.${i}.name`} control={control} render={({ field }) => (
                    <TextField {...field} label="Nume" fullWidth error={!!errors.contacts?.[i]?.name} helperText={errors.contacts?.[i]?.name?.message} />
                  )} />
                  <Controller name={`contacts.${i}.jobTitle`} control={control} render={({ field }) => (<TextField {...field} label="Funcție" fullWidth />)} />
                  <Controller name={`contacts.${i}.phone`} control={control} render={({ field }) => (<TextField {...field} label="Telefon" fullWidth />)} />
                  <Controller name={`contacts.${i}.email`} control={control} render={({ field }) => (
                    <TextField {...field} label="Email" fullWidth error={!!errors.contacts?.[i]?.email} helperText={errors.contacts?.[i]?.email?.message} />
                  )} />
                  <IconButton onClick={() => contacts.remove(i)}><DeleteOutlined fontSize="small" /></IconButton>
                </Stack>
              ))}
              <Box>
                <Button size="small" startIcon={<Add />} onClick={() => contacts.append({ id: null, name: '', jobTitle: '', phone: '', email: '' })}>
                  Adaugă contact
                </Button>
              </Box>
            </Stack>
          )}

          {Object.keys(errors).length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu (posibil pe alt tab).</Alert>
          )}
          {mutation.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(mutation.error)}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
